import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { DateTime } from "luxon"
import { DatePicker } from "@mui/x-date-pickers"
import { Grid } from "@mui/material"
import { CustomFilterMenuAccordion, FilterMenu } from "../../../../../../shared/components/filters/FilterMenu"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserType } from "../../../../../context/contextSelectors"
import { UserType } from "../../../../../login/loginTypes"
import useActiveFiltersCount from "../hooks/useActiveFiltersCount"
import { MerchantSubscriptionsFilter } from "../../components/inputFilters/MerchantSubscriptionsFilter"
import useFilterAccordions from "../../../../../../shared/components/filters/hooks/useFilterAccordions"
import { useDebouncedEffect } from "../../../../../../shared/hooks/useDebouncedEffect"
import { MultiMerchantSubscriptionsFilter } from "../../components/inputFilters/MultiMerchantSubscriptionsFilter"
import { StandardCustomerSelect } from "../../../../../../shared/components/filters/components/StandardCustomerSelect"
import { StandardEmailFilter } from "../../../../../../shared/components/filters/components/StandardEmailFilter"
import { StandardIdFilter } from "../../../../../../shared/components/filters/components/StandardIdFilter"
import { StandardLast4NumbersFilter } from "../../../../../../shared/components/filters/components/StandardLast4NumbersFilter"
import { StandardPaymentMethodsFilter } from "../../../../../../shared/components/filters/components/StandardPaymentMethodsFilter"
import { StandardMerchantsMultiselectFilter } from "../../../../../../shared/components/filters/components/StandardMerchantsMultiselectFilter"
import { StandardMerchantShopsFilter } from "../../../../../../shared/components/filters/components/StandardMerchantShopsFilter"
import { StandardMultiMerchantShopsFilter } from "../../../../../../shared/components/filters/components/StandardMultiMerchantShopsFilter"

export type SubscriptionsFilterProps = Readonly<
    { startDate: DateTime; endDate: DateTime } & Partial<{
        orderId: string
        merchantId: string
        merchantIds: string[]
        subscriptionIds: string[]
        shopIds: string[]
        customerId: string
        userGroupId: string
        employeeId: string
        email: string
        paymentMethods: string[]
        creditCardLast4: string
    }>
>

export const defaultSubscriptionsFilterOptionsList: SubscriptionsFilterProps = {
    orderId: "",
    startDate: DateTime.now(),
    endDate: DateTime.now(),
    merchantId: "",
    merchantIds: [],
    shopIds: [],
    subscriptionIds: [],
    customerId: "",
    userGroupId: "",
    employeeId: "",
    email: "",
    paymentMethods: [],
    creditCardLast4: "",
}

type FilterAccordions = {
    order: boolean
    period: boolean
    merchant: boolean
    debtor: boolean
    endUser: boolean
    payment: boolean
}

type FiltersDrawerContentSubscriptionsProps = {
    drawerOpen: boolean
    setDrawerOpen: (val: boolean) => void
    setFiltersList: Dispatch<SetStateAction<SubscriptionsFilterProps>>
    filtersList: SubscriptionsFilterProps
}

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
}))

export function FiltersDrawerSubscriptions({
    drawerOpen,
    setDrawerOpen,
    setFiltersList,
    filtersList,
}: FiltersDrawerContentSubscriptionsProps) {
    const { classes } = useStyles()
    const [optionsList, setOptionsList] = useState<SubscriptionsFilterProps>(filtersList)

    const { expandedAccordions, toggleAccordion } = useFilterAccordions<FilterAccordions>({
        order: false,
        period: false,
        merchant: false,
        debtor: false,
        endUser: false,
        payment: false,
    })

    const currentUserType = useAppSelector(selectEffectiveUserType)

    const { merchantActive, orderActive, debtorActive, endUserActive, paymentActive } =
        useActiveFiltersCount(optionsList)

    useEffect(() => {
        setOptionsList(filtersList)
    }, [filtersList])

    const handleMerchantClear = () => {
        setOptionsList((prevOptions) => {
            return {
                ...prevOptions,
                merchantId: currentUserType === UserType.MERCHANT ? optionsList.merchantId : "",
                merchantIds: [],
                customerId: "",
                shopIds: [],
                subscriptionIds: [],
            }
        })
    }

    const handleClearAll = () => {
        setOptionsList({
            ...defaultSubscriptionsFilterOptionsList,
            merchantId: currentUserType === UserType.MERCHANT ? optionsList.merchantId : "",
        })
    }

    const handelClearByType = (type: keyof SubscriptionsFilterProps) => {
        setOptionsList((prevOptions) => {
            return {
                ...prevOptions,
                [type]: defaultSubscriptionsFilterOptionsList[type],
            }
        })
    }

    useDebouncedEffect(() => {
        setFiltersList(optionsList)
    }, [optionsList, setFiltersList])

    const handleClose = () => {
        setDrawerOpen(false)
    }
    return (
        <FilterMenu
            drawerOpen={drawerOpen}
            handleClose={handleClose}
            handleClearAll={handleClearAll}
            isShowClearAllButton={true}
        >
            <LocalizedStrict id="supplier-subscriptions-table-filters-order-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Order"
                    expanded={expandedAccordions.order}
                    toggleAccordion={() => toggleAccordion("order")}
                    handleClear={() => {
                        handelClearByType("orderId")
                    }}
                    counter={orderActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardIdFilter
                            value={optionsList.orderId}
                            onChange={(orderId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    orderId,
                                }))
                            }
                            type="orderId"
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            <LocalizedStrict id="supplier-subscriptions-table-filters-period-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Period"
                    expanded={expandedAccordions.period}
                    toggleAccordion={() => toggleAccordion("period")}
                    handleClear={() => {}}
                    counter={0}
                >
                    <Grid container direction="column" gap="1rem">
                        <LocalizedStrict
                            id="supplier-subscriptions-table-filters-from-date-label"
                            attrs={{ label: true }}
                        >
                            <DatePicker
                                className={classes.input}
                                value={optionsList.startDate}
                                label="Valid from date"
                                onChange={(startDate) => {
                                    if (startDate) {
                                        setOptionsList((prev) => ({
                                            ...prev,
                                            startDate,
                                        }))
                                    }
                                }}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="supplier-subscriptions-table-filters-to-date-label"
                            attrs={{ label: true }}
                        >
                            <DatePicker
                                className={classes.input}
                                value={optionsList.endDate}
                                label="Valid to date"
                                onChange={(endDate) => {
                                    if (endDate) {
                                        setOptionsList((prev) => ({
                                            ...prev,
                                            endDate,
                                        }))
                                    }
                                }}
                            />
                        </LocalizedStrict>
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            <LocalizedStrict id="supplier-subscriptions-table-filters-merchant-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Merchant"
                    expanded={expandedAccordions.merchant}
                    toggleAccordion={() => toggleAccordion("merchant")}
                    handleClear={handleMerchantClear}
                    counter={merchantActive}
                >
                    <Grid container direction="column" gap="1rem">
                        {currentUserType !== UserType.MERCHANT && (
                            <StandardMerchantsMultiselectFilter
                                value={optionsList.merchantIds || []}
                                setValue={(merchantIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        merchantIds: merchantIds,
                                    }))
                                }}
                            />
                        )}

                        {currentUserType === UserType.MERCHANT ? (
                            <StandardMerchantShopsFilter
                                merchantId={optionsList.merchantId}
                                value={optionsList.shopIds || []}
                                setValue={(shopIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        shopIds,
                                    }))
                                }}
                            />
                        ) : (
                            <StandardMultiMerchantShopsFilter
                                merchantIds={optionsList.merchantIds || []}
                                selectedIds={optionsList.shopIds || []}
                                setSelectedIds={(shopIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        shopIds,
                                    }))
                                }}
                            />
                        )}

                        {currentUserType === UserType.MERCHANT ? (
                            <MerchantSubscriptionsFilter
                                merchantId={optionsList.merchantId}
                                value={optionsList.subscriptionIds || []}
                                setValue={(subscriptionIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        subscriptionIds,
                                    }))
                                }}
                            />
                        ) : (
                            <MultiMerchantSubscriptionsFilter
                                merchantIds={optionsList.merchantIds}
                                selectedIds={optionsList.subscriptionIds || []}
                                setSelectedIds={(subscriptionIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        subscriptionIds,
                                    }))
                                }}
                            />
                        )}
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>

            <LocalizedStrict id="supplier-subscriptions-table-filters-debtor-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Debtor"
                    expanded={expandedAccordions.debtor}
                    toggleAccordion={() => toggleAccordion("debtor")}
                    handleClear={() => {
                        handelClearByType("customerId")
                        handelClearByType("userGroupId")
                    }}
                    counter={debtorActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardCustomerSelect
                            setCustomerId={(customerId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    customerId,
                                }))
                            }
                            setCustomerGroupId={(userGroupId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    userGroupId,
                                }))
                            }
                            customerId={optionsList.customerId}
                            customerGroupId={optionsList.userGroupId}
                            merchantIdValue={optionsList.merchantId}
                            isFullWidth
                            isFilterDrawer
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            <LocalizedStrict id="supplier-subscriptions-table-filters-end-user-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="End user"
                    expanded={expandedAccordions.endUser}
                    toggleAccordion={() => toggleAccordion("endUser")}
                    handleClear={() => {
                        handelClearByType("email")
                        handelClearByType("employeeId")
                    }}
                    counter={endUserActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardEmailFilter
                            value={optionsList.email}
                            onChange={(email) => {
                                setOptionsList((prev) => ({
                                    ...prev,
                                    email,
                                }))
                            }}
                        />
                        <StandardIdFilter
                            value={optionsList.employeeId}
                            onChange={(employeeId) => {
                                setOptionsList((prev) => ({
                                    ...prev,
                                    employeeId,
                                }))
                            }}
                            type="employeeId"
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            <LocalizedStrict id="supplier-subscriptions-table-filters-payment-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Payment"
                    expanded={expandedAccordions.payment}
                    toggleAccordion={() => toggleAccordion("payment")}
                    handleClear={() => {
                        handelClearByType("creditCardLast4")
                        handelClearByType("paymentMethods")
                    }}
                    counter={paymentActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardPaymentMethodsFilter
                            value={optionsList.paymentMethods || []}
                            setValue={(paymentMethods) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    paymentMethods,
                                }))
                            }
                        />
                        <StandardLast4NumbersFilter
                            value={optionsList.creditCardLast4}
                            onChange={(creditCardLast4) => {
                                setOptionsList((prev) => ({
                                    ...prev,
                                    creditCardLast4,
                                }))
                            }}
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
        </FilterMenu>
    )
}
